import { supabase } from '../utils/supabase';

/**
 * Fetches the calling player's activity moments from the persisted activity_events
 * table via the get_player_activity_feed RPC.
 *
 * Returns a merged timeline across all clubs the player belongs to, newest first.
 *
 * @param {object} opts
 * @param {number}      [opts.limit=20]  Max events per page (server caps at 50).
 * @param {string|null} [opts.before]    occurred_at of last seen event for cursor pagination.
 * @returns {Promise<Array>}
 */
export async function fetchPlayerActivityFeed({ limit = 20, before = null } = {}) {
  const { data, error } = await supabase.rpc('get_player_activity_feed', {
    p_limit: limit,
    p_before: before ?? null,
  });

  if (error) {
    console.error('[ActivityFeed] Error fetching feed:', error);
    throw error;
  }

  const events = data || [];
  if (!events.length) return events;

  const clubIds = [...new Set(events.map((e) => e.club_id).filter(Boolean))];
  const { data: clubs, error: clubsError } = await supabase
    .from('clubs')
    .select('id, name, logo_image_url')
    .in('id', clubIds);

  if (clubsError) {
    console.error('[ActivityFeed] Error fetching club names:', clubsError);
    return events;
  }

  const nameById = Object.fromEntries((clubs || []).map((c) => [c.id, c.name]));
  const logoById = Object.fromEntries((clubs || []).map((c) => [c.id, c.logo_image_url ?? null]));
  return events.map((e) => ({
    ...e,
    club_name: nameById[e.club_id] ?? null,
    club_logo_image_url: logoById[e.club_id] ?? null,
  }));
}

/**
 * Ladder type → human-readable label for rendering event metadata.
 */
export const LADDER_LABELS = {
  mixed: 'Mixed Doubles',
  levels_male: "Men's Doubles",
  levels_female: "Women's Doubles",
  singles_male: "Men's Singles",
  singles_female: "Women's Singles",
};

export function getLadderLabel(ladderType) {
  return LADDER_LABELS[ladderType] || ladderType;
}

/** Formats a list of first names for display (e.g. "Alex & Sam"). */
export function formatFirstNames(names) {
  if (!names?.length) return null;
  const list = Array.isArray(names) ? names.filter(Boolean) : [];
  if (!list.length) return null;
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} & ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

/**
 * Optional plain-text subline for moment cards. upset_win uses NotableWinSubline (gender colours).
 */
export function buildEventSubline(event) {
  if (event?.event_type === 'upset_win') return null;
  return null;
}

/** Matchup event types that name an opponent in the headline. */
export function isOpponentNamedMatchupEvent(eventType) {
  return (
    eventType?.startsWith('matchup_')
    || ['rivalry_swing', 'rivalry_heated', 'rivalry_nemesis', 'rivalry_dominated', 'rivalry_emerging'].includes(
      eventType
    )
  );
}

/**
 * Derives a human-readable headline string for a single activity event.
 * All copy is generated client-side; the DB only stores structured facts.
 *
 * @param {object} event  Row from activity_events / get_player_activity_feed
 * @param {string} [displayNameFallback='Someone'] Name of the overtaken player if available.
 */
export function buildEventHeadline(event, overtakenPlayerName = null) {
  const { event_type, metadata } = event;
  const ladder = getLadderLabel(metadata?.ladder_type);

  switch (event_type) {
    case 'rank_up':
      return `Climbed ${metadata.rank_delta} place${metadata.rank_delta !== 1 ? 's' : ''} in ${ladder}`;
    case 'overtook_player':
      return overtakenPlayerName
        ? `Overtook ${overtakenPlayerName} in ${ladder}`
        : `Moved up in ${ladder}`;
    case 'personal_best':
      return `New personal best in ${ladder} — ${metadata.new_elo} rating`;
    case 'great_session':
      return `Great session: ${metadata.wins} of ${metadata.games} games won in ${ladder}`;
    case 'undefeated_session':
      return `Undefeated session: ${metadata.games} straight wins in ${ladder}`;
    case 'biggest_mover':
      return `Biggest mover in ${ladder} this week`;

    // Matchup transition events (legacy rivalry_* names — kept for backward compatibility
    // with rows written before migration 078; Phase 2 replaces these with matchup_* types)
    case 'rivalry_swing': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      if (w === l) return `You and ${name} are all square: now ${w}–${l}`;
      return `You moved ahead vs ${name}: now ${w}–${l}`;
    }
    case 'rivalry_heated': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `You and ${name} are evenly matched — ${w}–${l} across ${metadata?.total_matches ?? 0} games`;
    }
    case 'rivalry_nemesis': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `A familiar matchup vs ${name} — you're ${w}–${l} this season`;
    }
    case 'rivalry_dominated': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `Strong record vs ${name} — ${w}–${l} this season`;
    }
    case 'rivalry_emerging': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `You and ${name} are building a history — ${w}–${l} so far`;
    }

    // Phase 2 matchup_* transition types (from migration 078)
    case 'matchup_first_win': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `First win vs ${name}: now ${w}–${l}`;
    }
    case 'matchup_tied': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      return `All square with ${name}: now ${w}–${w}`;
    }
    case 'matchup_took_lead': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const w    = metadata?.player_wins ?? 0;
      const l    = metadata?.opponent_wins ?? 0;
      return `You moved ahead vs ${name}: now ${w}–${l}`;
    }
    case 'matchup_hot_form': {
      const name    = metadata?.opponent_first_name || 'your opponent';
      const last4   = metadata?.last4_wins ?? 3;
      return `Strong run vs ${name}: Won ${last4} of your last 4 games`;
    }
    case 'matchup_frequent_recent': {
      const name = metadata?.opponent_first_name || 'your opponent';
      const m30  = metadata?.matches_30d ?? 4;
      return `You've seen ${name} a lot lately: ${m30} games this month`;
    }

    case 'upset_win': {
      const ps = metadata?.player_score ?? '';
      const os = metadata?.opponent_score ?? '';
      const scoreStr = ps !== '' && os !== '' ? ` (${ps}–${os})` : '';
      return `Notable win${scoreStr}`;
    }

    case 'close_game_loss': {
      const ps = metadata?.player_score ?? '';
      const os = metadata?.opponent_score ?? '';
      const scoreStr = ps !== '' && os !== '' ? ` ${ps}–${os}` : '';
      return `Went to the wire: lost${scoreStr}`;
    }

    case 'session_new_opponent': {
      const name = metadata?.opponent_first_name || 'a new opponent';
      return `First game vs ${name} this season`;
    }

    case 'session_played_with': {
      const count   = metadata?.opponent_count ?? 0;
      const games   = metadata?.game_count ?? 0;
      const oppStr  = count === 1 ? '1 opponent' : `${count} opponents`;
      const gameStr = games === 1 ? '1 game' : `${games} games`;
      return `Played ${gameStr} against ${oppStr} this session`;
    }

    default:
      return `Activity in ${ladder}`;
  }
}

/**
 * Returns an emoji icon for an event type (used as visual marker in the feed card).
 */
export function getEventIcon(eventType) {
  switch (eventType) {
    case 'rank_up':             return '↑';
    case 'overtook_player':     return '🏆';
    case 'personal_best':       return '⭐';
    case 'great_session':       return '🔥';
    case 'undefeated_session':  return '✨';
    case 'biggest_mover':       return '📈';
    case 'rivalry_swing':       return '📈';
    case 'rivalry_heated':      return '⚖️';
    case 'rivalry_nemesis':     return '👥';
    case 'rivalry_dominated':   return '📈';
    case 'rivalry_emerging':    return '📅';
    // Phase 2 matchup_* types (added by migration 078)
    case 'matchup_first_win':       return '🏸';
    case 'matchup_tied':            return '⚖️';
    case 'matchup_took_lead':       return '📈';
    case 'matchup_hot_form':        return '📅';
    case 'matchup_frequent_recent': return '👥';
    case 'upset_win':               return '⚡';
    case 'close_game_loss':         return '🏸';
    case 'session_new_opponent':    return '🤝';
    case 'session_played_with':     return '🏸';
    default:                        return '🏸';
  }
}

/** Returns true for matchup/rivalry event types, which navigate to H2H instead of session recap. */
export function isMatchupEvent(eventType) {
  return (eventType?.startsWith('matchup_') || eventType?.startsWith('rivalry_')) ?? false;
}
